import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Plus, Minus, Send, Loader2, CheckCircle2, Clock } from 'lucide-react';
import {
  getCrewRequirements,
  updateCrewRequirements,
  createCrewRequest,
  getCrewRequests
} from '../services/api';

const CrewRequirementsPanel = ({ workOrderId, workOrder, onUpdate }) => {
  const [requirements, setRequirements] = useState({
    crew_leader: 0,
    mechanic_a: 0,
    mechanic_b: 0,
    helper: 0,
    construction_worker: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [existingRequest, setExistingRequest] = useState(null);
  const [requestMode, setRequestMode] = useState('DRAFT');

  useEffect(() => {
    fetchData();
  }, [workOrderId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch crew requirements
      const reqRes = await getCrewRequirements(workOrderId);
      if (reqRes.data.crew_requirements) {
        setRequirements(prev => ({
          ...prev,
          ...reqRes.data.crew_requirements
        }));
      }

      // Check for existing crew request
      const crRes = await getCrewRequests({ 
        work_order_id: workOrderId,
        status: 'queued,ready,started'
      });
      if (crRes.data.crew_requests && crRes.data.crew_requests.length > 0) {
        setExistingRequest(crRes.data.crew_requests[0]);
      }
    } catch (err) {
      console.error('Failed to fetch crew requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (role, delta) => {
    setRequirements(prev => ({
      ...prev,
      [role]: Math.max(0, (prev[role] || 0) + delta)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCrewRequirements(workOrderId, requirements);
      toast.success('Crew requirements saved');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to save crew requirements:', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleQueueRequest = async () => {
    const total = Object.values(requirements).reduce((a, b) => a + b, 0);
    if (total === 0) {
      toast.error('Please set crew requirements first');
      return;
    }

    setQueueing(true);
    try {
      // First save requirements
      await updateCrewRequirements(workOrderId, requirements);
      
      // Then create crew request
      await createCrewRequest({
        work_order_id: workOrderId,
        mode: requestMode
      });
      
      toast.success('Crew request queued successfully');
      fetchData(); // Refresh to show existing request
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to queue crew request:', err);
      toast.error(err.response?.data?.detail || 'Failed to queue request');
    } finally {
      setQueueing(false);
    }
  };

  const totalCrew = Object.values(requirements).reduce((a, b) => a + b, 0);

  const roleLabels = {
    crew_leader: 'Crew Leader',
    mechanic_a: 'A Mechanic',
    mechanic_b: 'B Mechanic',
    helper: 'Helper',
    construction_worker: 'Construction Worker'
  };

  if (loading) {
    return (
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-6" data-testid="crew-requirements-panel">
      <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-blue-400" />
        Crew Requirements
      </h3>

      {/* Existing Request Banner */}
      {existingRequest && (
        <div className={`mb-4 p-3 rounded-lg border ${
          existingRequest.status === 'started' 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-amber-500/10 border-amber-500/30'
        }`}>
          <div className="flex items-center gap-2">
            {existingRequest.status === 'started' ? (
              <CheckCircle2 className="w-5 h-5 text-green-400" />
            ) : (
              <Clock className="w-5 h-5 text-amber-400" />
            )}
            <span className={existingRequest.status === 'started' ? 'text-green-300' : 'text-amber-300'}>
              Crew request {existingRequest.status === 'started' ? 'is active' : 'is queued'}
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            Mode: {existingRequest.mode} | Crew Size: {existingRequest.crew_size_total}
          </p>
        </div>
      )}

      {/* Role Inputs */}
      <div className="space-y-3 mb-6">
        {Object.entries(roleLabels).map(([role, label]) => (
          <div key={role} className="flex items-center justify-between">
            <span className="text-slate-300">{label}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleChange(role, -1)}
                disabled={requirements[role] === 0 || existingRequest}
                className="p-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Minus className="w-4 h-4 text-slate-300" />
              </button>
              <span className="w-8 text-center text-slate-100 font-semibold">
                {requirements[role]}
              </span>
              <button
                onClick={() => handleChange(role, 1)}
                disabled={existingRequest}
                className="p-1 bg-slate-700 hover:bg-slate-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4 text-slate-300" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between py-3 border-t border-slate-700 mb-4">
        <span className="text-slate-200 font-medium">Total Crew Size</span>
        <span className="text-xl font-bold text-blue-400">{totalCrew}</span>
      </div>

      {/* Mode Selection (only show if no existing request) */}
      {!existingRequest && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-400 mb-2">Canvass Mode</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="requestMode"
                value="DRAFT"
                checked={requestMode === 'DRAFT'}
                onChange={(e) => setRequestMode(e.target.value)}
                className="text-blue-600"
              />
              <span className="text-slate-300 text-sm">Draft (Direct)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="requestMode"
                value="INTEREST"
                checked={requestMode === 'INTEREST'}
                onChange={(e) => setRequestMode(e.target.value)}
                className="text-purple-600"
              />
              <span className="text-slate-300 text-sm">Interest (Bidding)</span>
            </label>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {!existingRequest && (
          <>
            <button
              onClick={handleSave}
              disabled={saving || totalCrew === 0}
              className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save Requirements
            </button>
            <button
              onClick={handleQueueRequest}
              disabled={queueing || totalCrew === 0}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {queueing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Queue Crew Request
            </button>
          </>
        )}
        {existingRequest && (
          <a
            href="/crew-requests"
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium flex items-center justify-center gap-2"
          >
            View in Queue
          </a>
        )}
      </div>
    </div>
  );
};

export default CrewRequirementsPanel;
